import React, { useEffect, useRef, useState } from "react";
import {
  Send, Upload, Pause, Play, RefreshCw, Mail, AlertCircle, CheckCircle2, X, Eye,
} from "lucide-react";
import { api } from "@/lib/api";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const DEFAULT_SUBJECT = "New: The internet radio platform that pays creators 100% of tips";
const DEFAULT_HTML = `<p>Hi {{first_name}},</p>
<p>I'm DJ Anup, founder of <strong>Fliboe FM</strong> (<a href="https://fliboe.com">https://fliboe.com</a>) — and the platform just went live with what I think is your readers' kind of story.</p>
<p>After 20 years watching Spotify and the labels strip creators bare, I built something different: every artist gets their own worldwide radio station in 4 minutes, keeps <strong>100% of listener tips</strong>, and owns the audience outright.</p>
<p>Quick angles your readers at <strong>{{publication}}</strong> might care about:</p>
<ul>
  <li><strong>"The un-Spotify"</strong> — 0% commission on creator tips (Patreon is 8%, Twitch 50%).</li>
  <li><strong>Local-business ad slots from $25</strong> — bringing back local radio for SMBs.</li>
  <li><strong>AI Auto-DJ</strong> that introduces every song without a human host.</li>
</ul>
<p>Press kit, founder photo, share cards: <a href="https://fliboe.com/promo">https://fliboe.com/promo</a></p>
<p>Happy to jump on a 10-min call this week if it's useful.</p>
<p>— Anup<br>Fliboe FM</p>`;

export default function AdminPressCard() {
  const [rows, setRows] = useState([]);            // parsed CSV
  const [parseErr, setParseErr] = useState("");
  const [busyParse, setBusyParse] = useState(false);
  const [name, setName] = useState("Fliboe Launch · Music Press");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [html, setHtml] = useState(DEFAULT_HTML);
  const [sender, setSender] = useState("anup@fliboe.com");
  const [mailing, setMailing] = useState("Fliboe FM, 410 Bay Street, Gordon, AL 36343");
  const [busySend, setBusySend] = useState(false);
  const [msg, setMsg] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [openId, setOpenId] = useState(null);
  const fileRef = useRef(null);

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/admin/press/campaigns", { headers: authHeaders() });
      setCampaigns(data || []);
    } catch (_) {}
  };
  useEffect(() => {
    let alive = true;
    (async () => { if (alive) await loadCampaigns(); })();
    const id = setInterval(loadCampaigns, 5000);   // live progress poll
    return () => { alive = false; clearInterval(id); };
  }, []);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setParseErr(""); setRows([]); setBusyParse(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/press/parse", fd, { headers: authHeaders() });
      setRows(data?.rows || []);
      setMsg(`Parsed ${data?.count} unique emails.`);
    } catch (err) {
      setParseErr(err?.response?.data?.detail || "Could not parse CSV.");
    }
    setBusyParse(false);
  };

  const previewFor = (r) => {
    const first = (r.contact || "").split(",")[0].trim().split(" ")[0] || "there";
    return html
      .replace(/{{first_name}}/g, first)
      .replace(/{{contact}}/g, r.contact || "the team")
      .replace(/{{publication}}/g, r.publication || "your outlet")
      .replace(/{{site}}/g, r.site || "");
  };

  const startCampaign = async () => {
    setMsg("");
    if (!rows.length) { setMsg("Upload your CSV first."); return; }
    if (!mailing.trim()) { setMsg("Add your mailing address — required by CAN-SPAM."); return; }
    if (!sender.includes("@")) { setMsg("Enter a valid sender on your verified Resend domain."); return; }
    if (!window.confirm(`Send campaign to ${rows.length} recipients? Throttled to ~100/day on Resend free tier.`)) return;
    setBusySend(true);
    try {
      const payload = {
        name, subject, html, sender, rows,
        mailing_address: mailing.trim(),
      };
      const { data } = await api.post("/admin/press/campaign", payload, { headers: authHeaders() });
      setMsg(`Campaign queued · ${data.queued} recipients · ${data.daily_cap}/day cap`);
      setRows([]); // clear staged rows so we don't accidentally re-send
      await loadCampaigns();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not start campaign.");
    }
    setBusySend(false);
  };

  return (
    <section className="mt-10" data-testid="admin-press-card">
      <h2 className="fl-display text-2xl font-bold mb-4 flex items-center gap-2">
        <Send className="w-5 h-5 text-emerald-500" /> Press outreach
      </h2>

      {/* Step 1: upload */}
      <div className="fl-card p-5 mb-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-[240px] flex-1">
            <div className="text-white font-medium">1 · Upload your press CSV</div>
            <div className="text-xs text-slate-400 mt-1">
              Accepts the music-press contact format (Blog / Site / Email / Contacts / Notes).
              Duplicate emails are removed automatically. Previously-unsubscribed addresses are skipped.
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onFile}
            data-testid="admin-press-csv-input"
          />
          <button onClick={onPickFile} disabled={busyParse} className="fl-btn ghost" data-testid="admin-press-csv-pick">
            {busyParse ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {busyParse ? "Parsing…" : rows.length ? `Replace · ${rows.length} loaded` : "Choose CSV"}
          </button>
        </div>
        {parseErr && (
          <div className="mt-3 flex items-start gap-2 text-sm text-red-400">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {parseErr}
          </div>
        )}
        {rows.length > 0 && (
          <div className="mt-4 text-xs text-slate-400">
            Sample: {rows.slice(0, 3).map((r) => r.email).join(" · ")} … +{Math.max(0, rows.length - 3)} more
          </div>
        )}
      </div>

      {/* Step 2: template */}
      <div className="fl-card p-5 mb-5">
        <div className="text-white font-medium mb-3">2 · Customize the press email</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <LabeledInput label="Campaign name (internal only)" value={name} onChange={setName} testid="admin-press-name" />
          <LabeledInput label="Sender (must be verified at resend.com)" value={sender} onChange={setSender} testid="admin-press-sender" />
        </div>
        <LabeledInput label="Subject line" value={subject} onChange={setSubject} testid="admin-press-subject" className="mt-3" />
        <div className="mt-3">
          <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">
            HTML body · tokens: {"{{first_name}} {{publication}} {{contact}} {{site}}"}
          </label>
          <textarea
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            className="fl-input mt-1.5 h-48 font-mono text-xs"
            data-testid="admin-press-html"
          />
        </div>
        <LabeledInput
          label="Mailing address (CAN-SPAM footer · required)"
          value={mailing}
          onChange={setMailing}
          placeholder="Fliboe FM, 123 Main St, City, ST 12345"
          testid="admin-press-mailing"
          className="mt-3"
        />
        {rows.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs fl-mono uppercase tracking-widest text-emerald-500 cursor-pointer flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Preview for {rows[0].publication || rows[0].email}
            </summary>
            <div
              className="mt-3 fl-card p-4 text-sm text-slate-300 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: previewFor(rows[0]) }}
              data-testid="admin-press-preview"
            />
          </details>
        )}
      </div>

      {/* Step 3: send */}
      <div className="fl-card p-5 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-[220px] flex-1">
          <div className="text-white font-medium">3 · Send</div>
          <div className="text-xs text-slate-400 mt-1">
            Throttled to ~1 email/sec · {rows.length || 0} recipients ready · Resend free tier caps at 100/day, so 173 emails ≈ 2 days.
          </div>
        </div>
        <button
          onClick={startCampaign}
          disabled={busySend || !rows.length}
          className="fl-btn"
          data-testid="admin-press-send"
        >
          <Send className="w-4 h-4" />
          {busySend ? "Queuing…" : `Send to ${rows.length || 0}`}
        </button>
      </div>
      {msg && <div className="text-xs text-emerald-400 mb-4" data-testid="admin-press-msg">{msg}</div>}

      {/* Campaigns */}
      <h3 className="fl-display text-xl font-bold mb-3">Campaigns</h3>
      {campaigns.length === 0 ? (
        <div className="fl-card p-6 text-center text-slate-500 fl-mono text-xs uppercase tracking-widest">
          // no campaigns yet
        </div>
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => (
            <CampaignRow key={c.campaign_id} c={c} onOpen={() => setOpenId(c.campaign_id)} onChange={loadCampaigns} />
          ))}
        </div>
      )}

      {openId && <CampaignDrawer campaignId={openId} onClose={() => setOpenId(null)} />}
    </section>
  );
}

function LabeledInput({ label, value, onChange, placeholder, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">{label}</label>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="fl-input mt-1.5"
        data-testid={testid}
      />
    </div>
  );
}

function CampaignRow({ c, onOpen, onChange }) {
  const [busy, setBusy] = useState(false);
  const pct = c.total ? Math.round(((c.sent + c.failed) / c.total) * 100) : 0;
  const colour = c.status === "complete" ? "bg-emerald-500" : c.status === "error" ? "bg-red-500" : "bg-emerald-500";
  const pause = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/press/campaigns/${c.campaign_id}/pause`, {}, { headers: authHeaders() });
      await onChange();
    } catch (_) {}
    setBusy(false);
  };
  const resume = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/press/campaigns/${c.campaign_id}/resume`, {}, { headers: authHeaders() });
      await onChange();
    } catch (_) {}
    setBusy(false);
  };
  return (
    <div className="fl-card p-4" data-testid={`press-campaign-${c.campaign_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={onOpen} className="text-left min-w-0 flex-1">
          <div className="text-white font-medium truncate">{c.name}</div>
          <div className="text-xs text-slate-400 mt-0.5 truncate">{c.subject}</div>
          <div className="text-[10px] fl-mono uppercase tracking-widest text-slate-500 mt-1">
            {c.status} · {c.sent}/{c.total} sent · {c.failed} failed · cap {c.daily_cap}/day
          </div>
        </button>
        <div className="flex gap-2">
          {c.status === "sending" && (
            <button onClick={pause} disabled={busy} className="fl-btn ghost !py-2 !px-3 text-xs">
              <Pause className="w-4 h-4" /> Pause
            </button>
          )}
          {c.status === "paused" && (
            <button onClick={resume} disabled={busy} className="fl-btn !py-2 !px-3 text-xs">
              <Play className="w-4 h-4" /> Resume
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className={`h-full transition-all ${colour}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CampaignDrawer({ campaignId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/press/campaigns/${campaignId}`, { headers: authHeaders() });
        if (alive) setData(data);
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [campaignId]);
  if (!data) return null;
  const counts = data.recipients.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-[#0a0a0a] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/10 p-4 flex items-center justify-between">
          <div>
            <div className="fl-mono text-[10px] tracking-widest uppercase text-emerald-500">// {data.campaign.status}</div>
            <div className="text-white font-bold truncate">{data.campaign.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
            <Stat label="Sent" value={counts.sent || 0} colour="text-emerald-400" />
            <Stat label="Queued" value={(counts.queued || 0) + (counts.sending || 0)} colour="text-emerald-400" />
            <Stat label="Failed" value={counts.failed || 0} colour="text-red-400" />
          </div>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {data.recipients.map((r) => (
              <div key={r.email} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-white/5">
                <div className="min-w-0 flex-1 truncate">
                  <span className="text-white">{r.email}</span>
                  {r.publication && <span className="text-slate-500"> · {r.publication}</span>}
                </div>
                <StatusPill status={r.status} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, colour }) {
  return (
    <div className="fl-card p-3">
      <div className="fl-mono text-[9px] tracking-widest uppercase text-slate-500">{label}</div>
      <div className={`text-2xl font-bold ${colour}`}>{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    sent: { c: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", I: CheckCircle2 },
    failed: { c: "bg-red-500/15 text-red-300 border-red-500/30", I: AlertCircle },
    sending: { c: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", I: RefreshCw },
    queued: { c: "bg-slate-500/15 text-slate-300 border-slate-500/30", I: Mail },
    paused: { c: "bg-slate-500/15 text-slate-300 border-slate-500/30", I: Pause },
    suppressed: { c: "bg-violet-500/15 text-violet-300 border-violet-500/30", I: X },
  };
  const m = map[status] || map.queued;
  const Icon = m.I;
  return (
    <span className={`fl-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border flex items-center gap-1 shrink-0 ${m.c}`}>
      <Icon className={`w-3 h-3 ${status === "sending" ? "animate-spin" : ""}`} /> {status}
    </span>
  );
}
